import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import {
    Mail,
    ArrowLeft,
    CheckCircle,
    AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useForgotPassword } from "@/features/auth/hooks/useForgotPassword";

const forgotPasswordSchema = z.object({
    email: z.string().email("Please enter a valid email address"),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export const ForgotPasswordPage = () => {
    const { isLoading, isSuccess, error, sendResetEmail, reset } =
        useForgotPassword();

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<ForgotPasswordFormData>({
        resolver: zodResolver(forgotPasswordSchema),
    });

    const onSubmit = async (data: ForgotPasswordFormData) => {
        await sendResetEmail(data.email);
    };

    if (isSuccess) {
        return (
            <div className="min-h-screen flex flex-col bg-background">
                <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
                    <div className="w-full max-w-md space-y-8">
                        <Card className="shadow-medium">
                            <CardHeader className="text-center pb-4">
                                <div className="mx-auto mb-4 rounded-full bg-success/10 p-4 w-fit">
                                    <CheckCircle className="h-8 w-8 text-success" />
                                </div>
                                <CardTitle className="text-xl">
                                    Check your email
                                </CardTitle>
                                <CardDescription>
                                    We've sent password reset instructions to
                                    your email address.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <p className="text-sm text-muted-foreground text-center">
                                    If you don't see the email, check your spam
                                    folder. The link will expire in 1 hour.
                                </p>
                                <div className="flex flex-col gap-2">
                                    <Button
                                        variant="outline"
                                        onClick={reset}
                                        className="w-full"
                                    >
                                        Send another email
                                    </Button>
                                    <Link to="/login">
                                        <Button
                                            variant="ghost"
                                            className="w-full gap-2"
                                        >
                                            <ArrowLeft className="h-4 w-4" />
                                            Back to sign in
                                        </Button>
                                    </Link>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-background">
            <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
                <div className="w-full max-w-md space-y-8">
                    <Card className="shadow-medium">
                        <CardHeader className="text-center pb-4">
                            <div className="mx-auto mb-4 rounded-full bg-primary/10 p-4 w-fit">
                                <Mail className="h-8 w-8 text-primary" />
                            </div>
                            <CardTitle className="text-xl">
                                Forgot your password?
                            </CardTitle>
                            <CardDescription>
                                Enter your email address and we'll send you a
                                link to reset your password.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form
                                onSubmit={handleSubmit(onSubmit)}
                                className="space-y-5"
                            >
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email address</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="you@example.com"
                                        {...register("email")}
                                        className={
                                            errors.email
                                                ? "border-destructive focus-visible:ring-destructive"
                                                : ""
                                        }
                                    />
                                    {errors.email && (
                                        <p className="text-sm text-destructive flex items-center gap-1.5">
                                            <AlertCircle className="h-3.5 w-3.5" />
                                            {errors.email.message}
                                        </p>
                                    )}
                                </div>

                                {error && (
                                    <Alert variant="destructive">
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>
                                            {error}
                                        </AlertDescription>
                                    </Alert>
                                )}

                                <Button
                                    type="submit"
                                    className="w-full h-11"
                                    loading={isLoading}
                                    loadingLabel="Sending..."
                                >
                                    Send reset link
                                </Button>

                                <Link to="/login">
                                    <Button
                                        variant="ghost"
                                        className="w-full gap-2"
                                    >
                                        <ArrowLeft className="h-4 w-4" />
                                        Back to sign in
                                    </Button>
                                </Link>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>

            <footer className="py-6 text-center">
                <p className="text-sm text-muted-foreground">
                    © {new Date().getFullYear()} My Professional Tax. All rights
                    reserved.
                </p>
            </footer>
        </div>
    );
};
